import { getLayoutSlice } from './get-displayed-photos';

import type { ExperiencePhoto } from '../types/experience';
import type { GalleryLayout } from '../types/gallery-layout';

type PreviewImageSource = 'desktop' | 'mobile';

const IMAGE_DIMENSIONS_REGEXP = /\/(?:fit-in\/)?(\d+)x(\d+)\//;
const PICSUM_DIMENSIONS_REGEXP = /\/(\d+)\/(\d+)(?:\?|$)/;

function extractDimensionsFromUrl(url?: string): { width: number; height: number } | null {
  if (!url) {
    return null;
  }

  const match = url.match(IMAGE_DIMENSIONS_REGEXP) ?? url.match(PICSUM_DIMENSIONS_REGEXP);
  if (match === null) {
    return null;
  }

  const width = Number.parseInt(match[1] ?? '', 10);
  const height = Number.parseInt(match[2] ?? '', 10);

  if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
    return null;
  }

  return { width, height };
}

export function getPhotoAspectRatio(photo: ExperiencePhoto): number | null {
  const dimensions = extractDimensionsFromUrl(photo.medium || photo.thumbnail);
  if (dimensions === null) {
    return null;
  }

  return dimensions.width / dimensions.height;
}

// узкие плитки сетки (вертикальные в 3 колонки из 10 и квадратные в 2 колонки) показываются
// шириной 240-360px, и для них хватает thumbnail (600x800) вместо medium (1000x1350)
const NARROW_TILE_CLASSES = new Set(['vertical', 'square']);

export function getPreviewImageSrc(photo: ExperiencePhoto, source: PreviewImageSource, tileClass?: string): string {
  if (source === 'mobile' || (tileClass && NARROW_TILE_CLASSES.has(tileClass))) {
    return photo.thumbnail || photo.medium || '';
  }

  return photo.medium || photo.thumbnail || '';
}

export function getPreviewImageWidth(
  photo: ExperiencePhoto,
  source: PreviewImageSource,
  tileClass?: string,
): number | undefined {
  return extractDimensionsFromUrl(getPreviewImageSrc(photo, source, tileClass))?.width;
}

export function getPreviewImageHeight(
  photo: ExperiencePhoto,
  source: PreviewImageSource,
  tileClass?: string,
): number | undefined {
  return extractDimensionsFromUrl(getPreviewImageSrc(photo, source, tileClass))?.height;
}

// плитки, среди которых может оказаться самый крупный элемент страницы (LCP-кандидат).
// В horizontal-horizontal главное фото заметно крупнее остальных, в остальных раскладках
// рядом с ним стоит такая же по площади плитка (например, горизонтальная справа от вертикальной)
export function getPrimaryTilesCount(layout: GalleryLayout, photosCount: number): number {
  if (photosCount > 0 && photosCount < getLayoutSlice(layout)) {
    return photosCount === 2 ? 2 : 1;
  }

  return layout === 'horizontal-horizontal' ? 1 : 2;
}

export function getPreviewImageLoading(index: number, primaryCount = 1): 'eager' | 'lazy' {
  return index < primaryCount ? 'eager' : 'lazy';
}

export function getPreviewImageFetchPriority(index: number, primaryCount = 1): 'high' | 'low' {
  return index < primaryCount ? 'high' : 'low';
}

export function getDesktopPreviewImageSizes(index: number, layout: GalleryLayout): string {
  if (layout === 'square-square' && index <= 1) {
    return '(max-width: 767px) 0px, (max-width: 1279px) 50vw, 40vw';
  }

  if (index === 0 || (layout === 'vertical-vertical' && index === 1)) {
    return '(max-width: 767px) 0px, (max-width: 1279px) 60vw, 60vw';
  }

  return '(max-width: 767px) 0px, (max-width: 1279px) 25vw, 20vw';
}

export function getMobilePreviewImageSizes(): string {
  return '(max-width: 767px) calc(100vw - 48px), 0px';
}
